import { Application } from '@frontend/functionality/types';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName } from '@frontend/package/types';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    userTransactions: ExtendedPudoTransaction[] | null;
    pinStep?: Step;
    scanStep?: Step;
    onContinue: (nextStep?: Step | undefined) => void;
    onTransactionContinue: (transaction: ExtendedPudoTransaction) => void;
    transactionDataLoading: ExtendedPudoTransaction | null;
    onBack: (type: ScreenType) => void;
    isNivaliz: boolean;
}

const useAuthenticatedPickUpMethod = (): ViewProps => {
    const { store, onBack, onSubmit: onContinue, navigationState } = useScreen();
    const transactionContext = useWizardTransaction({
        wizardId: store!.wizard.id,
        store,
        autoCreate: false,
        autoFetch: false
    });
    const pkgContext = useWizardPackage({
        wizardId: store!.wizard.id,
        store,
        autoCreate: false,
        autoFetch: false
    });
    const [userTransactions, setUserTransactions] = useState<ExtendedPudoTransaction[] | null>(null);
    const [transactionDataLoading, setTransactionDataLoading] = useState<ExtendedPudoTransaction | null>(null);

    const receiverId = navigationState.user?.id;
    const spotId = navigationState.iot?.spot_id;

    useEffect(() => {
        if (!receiverId || !spotId) {
            setUserTransactions([]);
            return;
        }

        let cancelled = false;
        TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
            receiver_id: receiverId,
            state: TransactionStateName.TRANSACTION_PICK_UP_PENDING,
            spot_id: spotId,
            require_slot: 'true'
        })
            .then((res) => {
                if (!cancelled) setUserTransactions(res.results);
            })
            .catch(() => {
                if (!cancelled) setUserTransactions([]);
            });

        return () => {
            cancelled = true;
        };
    }, [receiverId, spotId]);

    useEffect(() => {
        transactionContext.localUpdateTransaction({ feature: store?.functionality.feature });
    }, []);

    const onTransactionContinue = async (transaction: ExtendedPudoTransaction) => {
        setTransactionDataLoading(transaction);
        const step = store?.steps.find(
            (step) =>
                step.screen === ScreenType.AUTHENTICATED_PICK_UP_METHOD &&
                step.trigger === ScreenTrigger.SUBMIT &&
                step.next_screen !== ScreenType.PICK_UP_PIN_CODE &&
                step.next_screen !== ScreenType.PICK_UP_SCAN_QR_CODE
        );
        try {
            const pkgs = await PackageClient.fetchPackages({
                transaction_id: transaction.id,
                type: 'pick_up',
                state: PackageStateName.PACKAGE_PICK_UP_PENDING
            });
            if (pkgs.results.length > 0) {
                await transactionContext.localUpdateTransaction(transaction);
                await pkgContext.localUpdatePackage(pkgs.results[0]);
                onContinue(step);
            }
        } finally {
            setTransactionDataLoading(null);
        }
    };

    return {
        userTransactions,
        pinStep: store?.steps.find(
            (step) =>
                step.screen === ScreenType.AUTHENTICATED_PICK_UP_METHOD &&
                step.trigger === ScreenTrigger.SUBMIT &&
                step.next_screen === ScreenType.PICK_UP_PIN_CODE
        ),
        scanStep: store?.steps.find(
            (step) =>
                step.screen === ScreenType.AUTHENTICATED_PICK_UP_METHOD &&
                step.trigger === ScreenTrigger.SUBMIT &&
                step.next_screen === ScreenType.PICK_UP_SCAN_QR_CODE
        ),
        onContinue,
        onTransactionContinue,
        transactionDataLoading,
        onBack,
        isNivaliz: store?.functionality.application === Application.NIVALIZ
    };
};

export default useAuthenticatedPickUpMethod;
